import { Calendar, TrendingUp, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { computeSteps, getSecondProgramSteps, MPR_TIMELINE_STEPS, TIMELINE_174_STEPS, formatCurrency } from "@/utils/simulateurUtils";

export function buildTimelinesForProgram({ programType, eligCumule, elig174, eligMPR }) {
  const today = new Date();
  let timelines = [];

  if (programType === "cumule" && eligCumule?.order) {
    const firstRaw = eligCumule.order[0] === "MPR" ? MPR_TIMELINE_STEPS : TIMELINE_174_STEPS;
    const secondRaw = eligCumule.order[0] === "MPR" ? TIMELINE_174_STEPS : MPR_TIMELINE_STEPS;
    const firstSteps = computeSteps(firstRaw, today);
    const firstEnd = firstSteps[firstSteps.length - 1].day;
    const secondFiltered = getSecondProgramSteps(secondRaw);
    const secondStart = new Date(today);
    secondStart.setDate(secondStart.getDate() + firstEnd);
    const secondSteps = computeSteps(secondFiltered, secondStart);
    secondSteps.forEach(s => s.day += firstEnd);
    timelines = [
      { label: eligCumule.order[0] === "MPR" ? "Programme MPR" : "Programme 174", steps: firstSteps, color: eligCumule.order[0] === "MPR" ? "blue" : "emerald" },
      { label: eligCumule.order[0] === "MPR" ? "Programme 174" : "Programme MPR", steps: secondSteps, color: eligCumule.order[0] === "MPR" ? "emerald" : "blue" }
    ];
  } else if (programType === "174" && elig174?.eligible !== false) {
    timelines = [{ label: "Programme 174", steps: computeSteps(TIMELINE_174_STEPS, today), color: "emerald" }];
  } else if (programType === "mpr" && eligMPR?.eligible !== false) {
    timelines = [{ label: "Programme MPR", steps: computeSteps(MPR_TIMELINE_STEPS, today), color: "blue" }];
  }

  if (timelines.length === 0) return { timelines: [], totalDays: 0 };
  const allSteps = timelines.flatMap(t => t.steps);
  const totalDays = Math.max(...allSteps.map(s => s.day));
  return { timelines, totalDays };
}

export function TimelineDisplay({ timelines, totalDays }) {
  if (!timelines || timelines.length === 0) return null;

  return (
    <div className="border border-border rounded-xl bg-card p-5 space-y-4" data-testid="frise-chronologique">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <Calendar className="w-4 h-4 text-orange-500" /> Frise chronologique du montage
        </h3>
        <span className="text-xs font-bold text-orange-600 bg-orange-100 px-2.5 py-0.5 rounded-full">{totalDays} jours</span>
      </div>
      {timelines.map((tl, tIdx) => {
        const tlTotal = tl.steps[tl.steps.length - 1].day - tl.steps[0].day;
        const bgColor = tl.color === "blue" ? "bg-blue-500" : "bg-emerald-500";
        const bgColorLight = tl.color === "blue" ? "bg-blue-100" : "bg-emerald-100";
        const textColor = tl.color === "blue" ? "text-blue-700" : "text-emerald-700";
        const lineColor = tl.color === "blue" ? "bg-blue-300" : "bg-emerald-300";
        const borderColor = tl.color === "blue" ? "border-blue-500/30" : "border-emerald-500/30";
        return (
          <div key={tIdx} className="space-y-2">
            <div className="flex items-center gap-2">
              <div className={cn("w-2.5 h-2.5 rounded-full", bgColor)} />
              <span className={cn("text-[11px] font-bold uppercase tracking-wide", textColor)}>{tl.label}</span>
              <span className="text-[10px] text-muted-foreground">({tlTotal} jours)</span>
            </div>
            <div className="flex items-start flex-wrap gap-y-3">
              {tl.steps.map((step, i) => {
                const gap = i < tl.steps.length - 1 ? tl.steps[i + 1].day - step.day : 0;
                const dateStr = step.date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
                return (
                  <div key={i} className="flex items-start">
                    <div className="flex flex-col items-center" style={{ minWidth: '68px' }}>
                      <span className={cn("text-[10px] font-bold mb-1", textColor)}>J{step.day}</span>
                      <div className={cn("w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white shadow z-10", bgColor)}>
                        {i + 1}
                      </div>
                      <div className={cn("mt-1.5 rounded-md px-1.5 py-1 text-center border shadow-sm", bgColorLight, borderColor)} style={{ minWidth: '64px', maxWidth: '80px' }}>
                        {step.labels.map((l, j) => (
                          <div key={j} className={cn("text-[9px] font-semibold leading-tight", textColor)}>{l}</div>
                        ))}
                        <span className="text-[8px] text-muted-foreground">{dateStr}</span>
                      </div>
                    </div>
                    {gap > 0 && (
                      <div className="flex flex-col items-center justify-center mt-4 mx-0.5">
                        <div className={cn("h-[2px] rounded-full", lineColor)} style={{ width: `${Math.max(16, gap * 3)}px` }} />
                        <span className="text-[8px] font-semibold text-muted-foreground">+{gap}j</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {tIdx < timelines.length - 1 && (
              <div className="flex items-center gap-2 pl-3 py-0.5">
                <ArrowRight className="w-3.5 h-3.5 text-violet-500" />
                <span className="text-[10px] text-violet-600 font-semibold">Enchaînement vers {timelines[tIdx + 1].label}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function CashFlowTable({ cashEvents, regieMode }) {
  if (!cashEvents || cashEvents.length === 0) return null;

  return (
    <div className="border border-border rounded-xl bg-card p-6 space-y-4" data-testid="frise-tresorerie">
      <h3 className="font-semibold text-base flex items-center gap-2">
        <TrendingUp className="w-5 h-5 text-orange-500" /> Flux de trésorerie
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="border-b-2 border-border">
              <th className="text-left py-2.5 px-3 font-semibold text-muted-foreground">Jour</th>
              <th className="text-left py-2.5 px-3 font-semibold text-muted-foreground">Date</th>
              <th className="text-left py-2.5 px-3 font-semibold text-muted-foreground">Programme</th>
              <th className="text-left py-2.5 px-3 font-semibold text-muted-foreground">Événement</th>
              <th className="text-right py-2.5 px-3 font-semibold text-muted-foreground">Montant</th>
              <th className="text-right py-2.5 px-3 font-semibold text-muted-foreground">Solde cumulé</th>
            </tr>
          </thead>
          <tbody>
            {cashEvents.map((ev, i) => (
              <tr key={i} className={cn("border-b border-border/40 transition-colors", ev.amount >= 0 ? "bg-emerald-500/10/30" : "bg-red-500/10/30")}>
                <td className="py-2.5 px-3 font-bold">J{ev.day}</td>
                <td className="py-2.5 px-3 text-muted-foreground">{ev.dateStr}</td>
                <td className="py-2.5 px-3">
                  <span className={cn("px-2 py-0.5 rounded text-[10px] font-bold uppercase", ev.prog === "MPR" ? "bg-blue-500/20 text-blue-600 dark:text-blue-400" : "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400")}>{ev.prog}</span>
                </td>
                <td className="py-2.5 px-3 font-medium">{ev.label}</td>
                <td className={cn("py-2.5 px-3 text-right font-bold", ev.amount >= 0 ? "text-emerald-600" : "text-red-600")}>
                  {ev.amount >= 0 ? "+" : ""}{formatCurrency(ev.amount)}
                </td>
                <td className={cn("py-2.5 px-3 text-right font-bold", ev.balance >= 0 ? "text-emerald-700" : "text-red-700")}>
                  {formatCurrency(ev.balance)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-border bg-muted/30">
              <td colSpan={4} className="py-3 px-3 font-bold text-sm">Solde final</td>
              <td className="py-3 px-3"></td>
              <td className={cn("py-3 px-3 text-right font-bold text-base", cashEvents[cashEvents.length - 1]?.balance >= 0 ? "text-emerald-700" : "text-red-700")}>
                {formatCurrency(cashEvents[cashEvents.length - 1]?.balance || 0)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="flex items-center gap-4 text-xs text-muted-foreground pt-1">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Encaissement</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> Décaissement</span>
        <span>Mode : {regieMode === "regie" ? "Régie (paiement au moment du Paiement programme)" : "Lead (paiement à J60)"}</span>
      </div>
    </div>
  );
}
